import type { BillOut } from "../../../../../api/bills";
import { roundToCent } from "./itemDiscountMath";

/** Бързите суми в € — сегментите под степера (с „Без" отпред). */
export const QUICK_AMOUNTS = [5, 10, 15, 20, 25, 50];

const parseMoney = (value: number | string | undefined | null): number =>
  value === undefined || value === null ? 0 : parseFloat(String(value)) || 0;

/** Въведената отстъпка в € на сметката (0 — няма). */
export const requestedBillDiscount = (
  bill: Pick<BillOut, "bill_discount_amount"> | undefined,
): number => roundToCent(parseMoney(bill?.bill_discount_amount));

/** Действащата отстъпка в € — въведената, но не над сбора на редовете (BE). */
export const appliedBillDiscount = (
  bill: Pick<BillOut, "bill_discount_applied"> | undefined,
): number => roundToCent(parseMoney(bill?.bill_discount_applied));

/** Сборът, който отстъпката на сметката може да намали — всички редове с
 *  отстъпките им по ред, и тези със заключена цена (като апарата, който я
 *  разнася по целия бон; BE `discountable_total`). */
export const discountableTotal = (totals: number[]): number =>
  roundToCent(totals.reduce((sum, value) => sum + value, 0));

/** Действащата при даден сбор: въведената, но не повече от него (като BE). */
export const effectiveAmount = (requested: number, discountable: number): number =>
  roundToCent(Math.max(0, Math.min(requested, discountable)));

/** До стотинка, между 0 и тавана. */
export const clampAmount = (value: number, max: number): number =>
  roundToCent(Math.min(Math.max(0, value), Math.max(0, max)));

/** Въведеното в полето: цифри и ЕДНА десетична точка (запетаята става
 *  точка). Невалидното се изрязва, не се отказва. */
export const sanitizeAmountInput = (raw: string): string => {
  const normalized = raw.replace(",", ".").replace(/[^0-9.]/g, "");
  const [whole, ...rest] = normalized.split(".");
  if (rest.length === 0) return whole;
  return `${whole}.${rest.join("")}`;
};

/** Изчистеното въведено → сума до стотинка, половинките нагоре (като BE
 *  `ROUND_HALF_UP`). Смята по ТЕКСТА: 12.345 в двоична запетая е 12.3449…
 *  и `Math.round` би дал 12.34. Повече от два знака след точката (трети
 *  написан или поставен) → закръглената сума с два знака и в полето. */
export const parseAmountInput = (
  clean: string,
): { amount: number; draft: string } => {
  const [whole = "", decimals] = clean.split(".");
  if (decimals === undefined || decimals.length <= 2) {
    return { amount: parseFloat(clean) || 0, draft: clean };
  }
  let cents = Number(whole || "0") * 100 + Number(decimals.slice(0, 2));
  if (Number(decimals[2]) >= 5) cents += 1;
  const amount = cents / 100;
  return { amount, draft: amount.toFixed(2) };
};
